import { useDebounce } from '@brandfolder/react';
import { Editor } from '@tinymce/tinymce-react';
import { FunctionComponent, useContext, useEffect, useRef, useState } from 'react';
import { Editor as TinyMceEditor } from 'tinymce';

import { HtmlFontLoader } from '@components/blocks/html/html-font-loader';
import { DragAndDropContext } from '@context/drag-and-drop';
import { GoogleWebfontsContext } from '@context/google-webfonts';
import { PublishContext } from '@context/publish';
import { Block } from '@typings/block';
import { Font } from '@typings/font';
import { getBlockDataIsHtml } from '@utilities/block';
import {
  addFontFamilyQuotes,
  getComputedFontFamily,
  getFirstFontFamily,
  getFontCategory,
  searchFonts,
  stripFontFamilyQuotes
} from '@utilities/font';

interface EditorRef {
  execCommand: (command: string) => void;
  getContent: () => string;
}

interface TinyMceInlineProps {
  block: Block;
  onBlur: (content: string) => void;
  disabled?: boolean;
  isServer?: boolean;
}

const placeholder = 'Search fonts...';
const tooltip = 'Font Family';

const getFontFamilyLabel = (): HTMLSpanElement | null => {
  const selector = `button[title="${tooltip}"] > .tox-tbtn__select-label`;
  return document.querySelector(selector);
};

const getFontFamilyButtonText = (): string => {
  return getFontFamilyLabel()?.textContent || tooltip;
};

/**
 * There is no programmatic way to clear the search input with TinyMCE.
 * Closing (which happens clicking "No results") then reopening does this for us.
 */
const clearFontFamilySearchInput = (): void => {
  const button = document.querySelector(`button[title="${tooltip}"]`);
  if (button) {
    setTimeout(() => {
      (button as HTMLButtonElement).click();
    }, 0); // needed for whatever reason
  }
};

const updateFontFamilyLabelText = (fontFamily: string): void => {
  const label = getFontFamilyLabel();
  if (label) {
    label.innerHTML = fontFamily;
  }
};

/**
 * Copied from node_modules/tinymce/tinymce.d.ts:759
 * (Not exported by TinyMCE)
 */
interface ToggleMenuItemSpec {
  active: boolean;
  onAction: () => void;
  text: string;
  type: 'togglemenuitem';
}

const handleFonts = (
  editor: TinyMceEditor,
  fonts: Font[],
  search?: string
): ToggleMenuItemSpec[] => {
  const searched = searchFonts(fonts, search);

  if (searched.length === 0)
    return [
      {
        active: false,
        onAction: () => clearFontFamilySearchInput(),
        text: 'No results',
        type: 'togglemenuitem'
      }
    ];

  return searched.map((font) => {
    return {
      active: getFontFamilyButtonText() === font.family,
      onAction: () => {
        editor.formatter.apply('fontname', {
          value: `${addFontFamilyQuotes(font.family)},${getFontCategory(font.category)}`
        });
        editor.fire('change');
        updateFontFamilyLabelText(font.family);
      },
      text: font.family,
      type: 'togglemenuitem'
    };
  });
};

/**
 * IMPORTANT: TinyMCE should not be loaded server-side.
 * This means a user needs to be logged in and editing
 * in order to use this component.
 *
 * @param props TinyMceInlineProps
 * @returns FunctionComponent<TinyMceInlineProps>
 */
export const TinyMceInline: FunctionComponent<TinyMceInlineProps> = (props) => {
  const { block, disabled = false, isServer = false, onBlur } = props;

  const editorRef = useRef<EditorRef | null>(null);

  const { dragging, reordering } = useContext(DragAndDropContext);
  const { fonts } = useContext(GoogleWebfontsContext);
  const { autoFocusKey, setAutoFocusKey } = useContext(PublishContext);

  const [localBlock, setLocalBlock] = useState<Block>(block);

  const debouncedLocalBlock = useDebounce(localBlock, 300);

  useEffect(() => {
    if (autoFocusKey && autoFocusKey === block.key) {
      setAutoFocusKey('');
    }
  }, [autoFocusKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const data = getBlockDataIsHtml(block.data) ? block.data : null;

  if (!data || isServer) return null;

  return (
    <HtmlFontLoader block={debouncedLocalBlock}>
      <Editor
        data-testid="tiny-mce"
        disabled={disabled || dragging || !!reordering}
        id={block.key}
        init={{
          auto_focus: autoFocusKey,
          font_size_formats: '10px 12px 14px 16px 18px 24px 36px 48px 60px 72px',
          indentation: '30px',
          inline: true,
          line_height_formats: '1 1.2 1.4 1.6 2',
          menubar: false,
          plugins: ['anchor', 'charmap', 'emoticons', 'link', 'lists', 'table', 'wordcount'],
          relative_urls: false,
          schema: 'html5',
          setup: (editor) => {
            /**
             * https://www.tiny.cloud/docs/tinymce/6/events/#editor-core-events
             */
            editor.on('NodeChange', (e) => {
              updateFontFamilyLabelText(
                stripFontFamilyQuotes(getFirstFontFamily(getComputedFontFamily(e.element)))
              );
            });

            /**
             * https://www.tiny.cloud/docs/tinymce/6/custom-menu-toolbar-button/#searchable-menu-buttons
             */
            editor.ui.registry.addMenuButton('fontfamilies', {
              fetch: (success, fetchContext) => {
                success(handleFonts(editor, fonts, fetchContext.pattern));
              },
              search: {
                placeholder
              },
              text: '',
              tooltip,
              type: 'menubutton'
            });
          },
          /**
           * https://www.tiny.cloud/docs/configure/editor-appearance/#toolbar
           * NOTE: `fontfamilies` is a custom menu button
           */
          toolbar: [
            'blocks fontfamilies fontsize lineheight | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify',
            'forecolor backcolor removeformat | bullist numlist indent outdent | link unlink anchor | blockquote charmap emoticons | table | wordcount'
          ],
          // https://www.tiny.cloud/docs/configure/editor-appearance/#style_formats
          style_formats: [
            {
              title: 'Headings',
              items: [
                { title: 'Heading 1', format: 'h1' },
                { title: 'Heading 2', format: 'h2' },
                { title: 'Heading 3', format: 'h3' },
                { title: 'Heading 4', format: 'h4' },
                { title: 'Heading 5', format: 'h5' },
                { title: 'Heading 6', format: 'h6' }
              ]
            },
            {
              title: 'Inline',
              items: [
                { title: 'Bold', format: 'bold' },
                { title: 'Italic', format: 'italic' },
                { title: 'Underline', format: 'underline' },
                { title: 'Strikethrough', format: 'strikethrough' },
                { title: 'Superscript', format: 'superscript' },
                { title: 'Subscript', format: 'subscript' }
              ]
            },
            {
              title: 'Align',
              items: [
                { title: 'Left', format: 'alignleft' },
                { title: 'Center', format: 'aligncenter' },
                { title: 'Right', format: 'alignright' },
                { title: 'Justify', format: 'alignjustify' }
              ]
            }
          ],
          valid_styles: {
            '*': 'background-color,border,border-collapse,border-color,border-spacing,border-style,border-width,color,font-family,font-size,font-style,font-weight,height,letter-spacing,line-height,margin,margin-bottom,margin-left,margin-right,margin-top,padding,padding-bottom,padding-left,padding-right,padding-top,text-align,text-decoration,text-shadow,text-transform,width,word-spacing'
          }
        }}
        initialValue={data.html}
        onInit={(_, editor) => (editorRef.current = editor)}
        onBlur={() => {
          onBlur(editorRef.current?.getContent() || '');
        }}
        onChange={() => {
          setLocalBlock({ ...localBlock, data: { html: editorRef.current?.getContent() || '' } });
        }}
        tinymceScriptSrc={'/tinymce/tinymce.min.js'}
      />
    </HtmlFontLoader>
  );
};
